import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import {
  useGetLegalEntities,
  useGetNursingHomes,
  useGetTenantInformation,
} from '@pflegenavi/frontend/api-nursing-home';
import { useScope } from './ScopeContext';
import { useNursingHomeAdminScope } from './employeeRoles';

/**
 * Loads everything the scope can be set to and feeds it into the
 * {@link ScopeProvider}.
 *
 * The provider itself cannot fetch: it is mounted above the API providers,
 * because most of the tree depends on the scope. So it owns the state and this
 * component, rendered inside the API providers, owns the loading.
 *
 * @example
 * <NursingHomePhoenixApiProvider>
 *   <ScopeDataSync />
 * </NursingHomePhoenixApiProvider>
 */
export const ScopeDataSync: FC = () => {
  const {
    setNursingHomeList,
    setLegalEntityList,
    setKnownLegalEntities,
    setTenant,
  } = useScope();

  const adminScope = useNursingHomeAdminScope();

  const { data: nursingHomes, isLoading: isLoadingNursingHomes } =
    useGetNursingHomes({
      refetchOnWindowFocus: false,
    });
  const { data: legalEntities } = useGetLegalEntities();
  const { data: tenantInformation } = useGetTenantInformation();

  const nursingHomeList = useMemo(
    () =>
      nursingHomes?.data?.map((nursingHome) => ({
        id: nursingHome.id,
        name: nursingHome.name,
        legalEntityId: nursingHome.legal_entity_id,
        pdfLogoId: nursingHome.pdf_logo_id,
        paymentsDisabled: nursingHome.payment_disabled,
        featureFlags: nursingHome.feature_flags,
        minPaymentAmountInCents: nursingHome.min_payment_amount,
        maxPaymentAmountInCents: nursingHome.max_payment_amount,
        minPaymentAmountInCentsEmployeeInitiated:
          nursingHome.min_payment_amount_employee_initiated,
        maxPaymentAmountInCentsEmployeeInitiated:
          nursingHome.max_payment_amount_employee_initiated,
        minResidentThresholdsValue: nursingHome.min_resident_thresholds_value,
        maxResidentThresholdsValue: nursingHome.max_resident_thresholds_value,
        costCenter: nursingHome.cost_center,
        legalEntity: nursingHome.legal_entity,
      })) ?? [],
    [nursingHomes]
  );

  const legalEntityList = useMemo(
    () =>
      (legalEntities?.data ?? []).map((legalEntity) => ({
        id: legalEntity.id,
        name: legalEntity.base_data.company,
        invoiceCostCenter: legalEntity.invoice_cost_center,
      })),
    [legalEntities]
  );

  // An unresolved query maps to an empty list just like an empty response does,
  // so the lists themselves cannot tell the two apart - and they must be, since
  // one means "wait" and the other means "clear what you had".
  const nursingHomesLoaded =
    !isLoadingNursingHomes && Boolean(nursingHomes?.data);
  const legalEntitiesLoaded = Boolean(legalEntities?.data);

  useEffect(() => {
    if (nursingHomesLoaded) {
      setNursingHomeList(nursingHomeList);
    }
  }, [nursingHomeList, nursingHomesLoaded, setNursingHomeList]);

  /**
   * The entities the employee may actually scope to: those where they are admin
   * or manager in at least one nursing home. A global tenant admin gets all of
   * them (ENG-2503).
   *
   * Filtering here rather than in the selector keeps one invariant - the scope
   * only ever holds an entity the employee is entitled to - so neither the
   * stored selection, the `?le_id=` link nor `useScopeFilter` can reach past it.
   */
  const permittedLegalEntityList = useMemo(() => {
    if (!adminScope) {
      return [];
    }
    if (adminScope.isGlobalTenantAdmin) {
      return legalEntityList;
    }

    const adminNursingHomeIds = new Set(adminScope.nursingHomeIds);
    const permittedIds = new Set(
      nursingHomeList
        .filter((nursingHome) => adminNursingHomeIds.has(nursingHome.id))
        .map((nursingHome) => nursingHome.legalEntityId)
    );

    return legalEntityList.filter((legalEntity) =>
      permittedIds.has(legalEntity.id)
    );
  }, [adminScope, nursingHomeList, legalEntityList]);

  useEffect(() => {
    // Wait for the role and for the entities themselves; filtering on a
    // half-loaded picture would drop a selection the employee still has. Once
    // both have arrived an empty result is meaningful - "nothing you may scope
    // to" - and must be passed on, so that a stored selection the employee is
    // no longer entitled to gets cleared rather than lingering in storage.
    if (!adminScope || !legalEntitiesLoaded) {
      return;
    }
    // The role maps to entities through the nursing homes, so everyone except a
    // tenant admin - who is entitled to all of them anyway - needs those too.
    if (!adminScope.isGlobalTenantAdmin && !nursingHomesLoaded) {
      return;
    }
    setLegalEntityList(permittedLegalEntityList);
  }, [
    adminScope,
    nursingHomesLoaded,
    legalEntitiesLoaded,
    permittedLegalEntityList,
    setLegalEntityList,
  ]);

  useEffect(() => {
    if (legalEntitiesLoaded) {
      setKnownLegalEntities(legalEntityList);
    }
  }, [legalEntityList, legalEntitiesLoaded, setKnownLegalEntities]);

  useEffect(() => {
    const name = tenantInformation?.data?.name;
    setTenant(name ? { name } : undefined);
  }, [tenantInformation, setTenant]);

  return null;
};
